'use client'

import { Cell, Image, Radio } from '@vinted/web-ui'
import { ChangeEvent, ReactNode, useState } from 'react'

import { List } from '@marketplace-web/common-components/list-ui'
import {
  InputDropdown,
  InputDropdownRenderProps,
} from '@marketplace-web/common-components/input-dropdown-ui'

type Props<T> = {
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onSave: (user: T) => void
  title?: string
  users: Array<T>
  placeholder?: string
  selectedUser?: T | null
  preselectedUser?: T | null
  disableSearch?: boolean
  name: string
  validation?: ReactNode
  isPhone: boolean
  textSave: string
  emptyUserImage: string
  testId?: string
}

const UserSelect = <T extends { id: number; login: string; thumbnail: string | null }>({
  title,
  users,
  placeholder,
  selectedUser,
  onChange,
  onSave,
  name,
  validation,
  preselectedUser,
  disableSearch,
  isPhone,
  textSave,
  emptyUserImage,
  testId,
}: Props<T>) => {
  const [selectedValue, setSelectedValue] = useState(preselectedUser || selectedUser)

  const handleUserSelect = (user: T, inputRenderProps: InputDropdownRenderProps) => () => {
    setSelectedValue(user)

    inputRenderProps.setIsSaveEnabled(true)

    if (isPhone) return

    inputRenderProps.closeDropdown()
    onSave(user)
  }

  function renderUser(user: T, inputRenderProps: InputDropdownRenderProps) {
    const isSelected = selectedValue?.id === user.id
    const labelId = `radio-label-${user.login}`

    return (
      <Cell
        key={user.id}
        title={user.login}
        id={labelId}
        clickable
        onClick={handleUserSelect(user, inputRenderProps)}
        prefix={
          <Image
            role="img"
            src={user.thumbnail || emptyUserImage}
            size={Image.Size.Regular}
            styling={Image.Styling.Circle}
          />
        }
        suffix={
          <div data-testid={`user-select-${user.login}`}>
            <Radio
              name="user-select"
              checked={isSelected}
              onChange={() => undefined}
              aria={{
                'aria-labelledby': labelId,
              }}
            />
          </div>
        }
      />
    )
  }

  function renderUserList(inputRenderProps: InputDropdownRenderProps) {
    if (!users) return null

    return <List>{users.map(user => renderUser(user, inputRenderProps))}</List>
  }

  function handleSave() {
    if (!selectedValue) return

    onSave(selectedValue)
  }

  function handleCancel() {
    setSelectedValue(selectedUser)
  }

  return (
    <InputDropdown
      title={title}
      textSave={textSave}
      placeholder={placeholder}
      name={name}
      render={renderUserList}
      onChange={onChange}
      onSave={handleSave}
      onCancel={handleCancel}
      value={selectedUser?.login}
      readOnly={disableSearch}
      disabled={!!preselectedUser}
      validation={validation}
      isPhone={isPhone}
      testId={testId}
    />
  )
}

export default UserSelect
